"use client";

import { c as _c } from "react/compiler-runtime";
import { getResolvedAnimationSrc } from "@/components/nft-image/utils/animation-source";
import { AdditionalDetailsSection, ArweaveLinkRow, EmptyDetailsState, MetadataCard, MetricBlock, type AdditionalDetailsMediaRow } from "@/components/the-memes/MemePageAdditionalDetails";
import type { IAttribute, LabNFT } from "@/entities/INFT";
import { parseNftDescriptionToHtml, printMintDate } from "@/helpers/Helpers";
import { getAnimationDimensionsFromMetadata, getAnimationFileTypeFromMetadata, getImageDimensionsFromMetadata, getImageFileTypeFromMetadata } from "@/helpers/nft.helpers";
import { ChartBarIcon, ChevronDownIcon, InformationCircleIcon, LinkIcon, SwatchIcon } from "@heroicons/react/24/outline";
import { type ReactNode, useState } from "react";
type MemeLabMediaMetadata = Parameters<typeof getImageFileTypeFromMetadata>[0];
type MemeLabMetadataLinks = {
  readonly image?: unknown;
  readonly animation?: unknown;
  readonly animation_url?: unknown;
};
const trimToEmpty = (value: unknown): string => typeof value === "string" ? value.trim() : "";
function getMemeLabMetadataAttributes(metadata: unknown): IAttribute[] {
  const attributes = metadata !== null && typeof metadata === "object" ? (metadata as {
    readonly attributes?: unknown;
  }).attributes : undefined;
  if (!Array.isArray(attributes)) {
    return [];
  }
  return attributes.filter((attribute): attribute is IAttribute => attribute !== null && typeof attribute === "object" && typeof (attribute as {
    readonly trait_type?: unknown;
  }).trait_type === "string" && (typeof (attribute as {
    readonly value?: unknown;
  }).value === "string" || typeof (attribute as {
    readonly value?: unknown;
  }).value === "number"));
}
function getMemeLabMediaMetadata(metadata: unknown): MemeLabMediaMetadata {
  if (metadata === null || metadata === undefined || typeof metadata === "string") {
    return metadata;
  }
  if (typeof metadata === "object") {
    return metadata;
  }
  return undefined;
}
function getMemeLabMetadataLinks(metadata: unknown): MemeLabMetadataLinks {
  return metadata !== null && typeof metadata === "object" ? metadata : {};
}
function getFirstNonEmptyValue(values: readonly string[]): string {
  return values.find(value => value.length > 0) ?? "";
}
function getMemeLabArweaveRows(nft: LabNFT): AdditionalDetailsMediaRow[] {
  const mediaMetadata = getMemeLabMediaMetadata(nft.metadata);
  const metadataLinks = getMemeLabMetadataLinks(nft.metadata);
  const imageFormat = getImageFileTypeFromMetadata(mediaMetadata);
  const animationFormat = getAnimationFileTypeFromMetadata(mediaMetadata);
  const metadataHref = trimToEmpty(nft.uri);
  const metadataImageHref = trimToEmpty(metadataLinks.image);
  const nftImageHref = trimToEmpty(nft.image);
  const metadataAnimationUrlHref = trimToEmpty(metadataLinks.animation_url);
  const metadataAnimationHref = trimToEmpty(metadataLinks.animation);
  const nftAnimationHref = trimToEmpty(nft.animation);
  const artImageHref = getFirstNonEmptyValue([metadataImageHref, nftImageHref]);
  const artAnimationHref = getFirstNonEmptyValue([metadataAnimationUrlHref, metadataAnimationHref, nftAnimationHref]);
  const nftName = trimToEmpty(nft.name);
  const artDownloadName = nftName.length > 0 ? nftName : `meme-lab-${nft.id}`;
  return [...(metadataHref ? [{
    label: "JSON",
    title: "JSON Metadata",
    url: metadataHref,
    openLabel: "Open JSON in new tab"
  }] : []), ...(artImageHref ? [{
    label: imageFormat?.toUpperCase() ?? "IMAGE",
    title: "Image Asset",
    url: artImageHref,
    openLabel: "Open image in new tab",
    extension: imageFormat ?? "",
    downloadName: artDownloadName
  }] : []), ...(artAnimationHref ? [{
    label: animationFormat?.toUpperCase() ?? "ANIMATION",
    title: "Animation Asset",
    url: artAnimationHref,
    openLabel: "Open animation in new tab",
    extension: animationFormat ?? "",
    downloadName: artDownloadName
  }] : [])];
}
function getMemeLabDetailRows(nft: LabNFT) {
  const mediaMetadata = getMemeLabMediaMetadata(nft.metadata);
  const hasAnimation = Boolean(getResolvedAnimationSrc(nft));
  const imageFormat = getImageFileTypeFromMetadata(mediaMetadata);
  const animationFormat = getAnimationFileTypeFromMetadata(mediaMetadata);
  const imageDimensions = getImageDimensionsFromMetadata(mediaMetadata);
  const animationDimensions = getAnimationDimensionsFromMetadata(mediaMetadata);
  const fileType = hasAnimation ? animationFormat : imageFormat;
  const dimensions = hasAnimation ? animationDimensions : imageDimensions;
  const fileMediaKind = hasAnimation ? "Animation" : "Image";
  const fileTypeLabel = fileType ? `${fileMediaKind} - ${fileType.toUpperCase()}` : "N/A";
  return [{
    key: "edition-size",
    label: "Edition size",
    value: nft.supply
  }, {
    key: "collection",
    label: "Collection",
    value: nft.collection
  }, {
    key: "mint-date",
    label: "Mint date",
    value: printMintDate(nft.mint_date)
  }, {
    key: "file-type",
    label: "File type",
    value: fileTypeLabel
  }, {
    key: "dimensions",
    label: "Dimensions",
    value: dimensions ?? "N/A"
  }];
}
function MemeLabAdditionalDetailsAccordion(t0) {
  const $ = _c(25);
  const {
    defaultOpen,
    children
  } = t0;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsOpen(_temp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-p-1.5 tw-transition-colors tw-duration-300 tw-ease-out ${isOpen ? "tw-bg-primary-500 tw-text-iron-100" : "tw-bg-iron-900 tw-text-iron-500 group-hover:tw-text-iron-100"}`;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <InformationCircleIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" />;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t2) {
    t4 = <span className={t2}>{t3}</span>;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-200">Additional details</span>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t4) {
    t6 = <span className="tw-flex tw-items-center tw-gap-3">{t4}{t5}</span>;
    $[5] = t4;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = `tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-iron-500 tw-transition tw-duration-200 group-hover:tw-text-iron-100 ${isOpen ? "tw-rotate-180 tw-text-iron-100" : ""}`;
  let t8;
  if ($[7] !== t7) {
    t8 = <ChevronDownIcon className={t7} />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== isOpen || $[10] !== t6 || $[11] !== t8) {
    t9 = <button type="button" aria-expanded={isOpen} onClick={t1} className="tw-group tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-4 tw-border-0 tw-bg-transparent tw-px-0 tw-py-4 tw-text-left tw-text-iron-100 tw-transition-colors tw-duration-300 tw-ease-out hover:tw-text-white">{t6}{t8}</button>;
    $[9] = isOpen;
    $[10] = t6;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = `tw-grid tw-transition-[grid-template-rows,opacity] tw-duration-300 tw-ease-out ${isOpen ? "tw-grid-rows-[1fr] tw-opacity-100" : "tw-grid-rows-[0fr] tw-opacity-0"}`;
  const t11 = !isOpen;
  const t12 = !isOpen;
  const t13 = isOpen ? undefined : -1;
  const t14 = isOpen ? "tw-overflow-visible" : "tw-overflow-hidden";
  let t15;
  if ($[13] !== children || $[14] !== t11 || $[15] !== t12 || $[16] !== t13 || $[17] !== t14) {
    t15 = <div aria-hidden={t11} inert={t12} tabIndex={t13} className={t14}>{children}</div>;
    $[13] = children;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== t10 || $[20] !== t15) {
    t16 = <div className={t10}>{t15}</div>;
    $[19] = t10;
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t16 || $[23] !== t9) {
    t17 = <section className="tw-mt-4 tw-border-x-0 tw-border-y tw-border-solid tw-border-iron-800">{t9}{t16}</section>;
    $[22] = t16;
    $[23] = t9;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  return t17;
}
function _temp(current) {
  return !current;
}
function MemeLabCardDescription(t0) {
  const $ = _c(4);
  const {
    nft
  } = t0;
  let t1;
  if ($[0] !== nft.description) {
    t1 = parseNftDescriptionToHtml(nft.description);
    $[0] = nft.description;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <section className="tw-max-w-4xl tw-text-pretty tw-pb-4"><div className="tw-text-base tw-font-normal tw-text-iron-300" dangerouslySetInnerHTML={{
        __html: t1
      }} /></section>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function MemeLabAdditionalDetailsContent(t0) {
  const $ = _c(20);
  const {
    nft
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== nft) {
    const arweaveRows = getMemeLabArweaveRows(nft);
    const detailRows = getMemeLabDetailRows(nft);
    const attributes = getMemeLabMetadataAttributes(nft.metadata);
    t4 = "tw-space-y-14 tw-pb-8 tw-pt-8";
    t5 = arweaveRows.length > 0 && <AdditionalDetailsSection title="Arweave links" icon={LinkIcon}><div>{arweaveRows.map(_temp2)}</div></AdditionalDetailsSection>;
    const t7 = detailRows.map(_temp3);
    if ($[8] !== t7) {
      t6 = <AdditionalDetailsSection title="Card details" icon={ChartBarIcon}><div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-6 sm:tw-grid-cols-3 sm:tw-gap-x-12 sm:tw-gap-y-8 xl:tw-grid-cols-5">{t7}</div></AdditionalDetailsSection>;
      $[8] = t7;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    T0 = AdditionalDetailsSection;
    t1 = "Properties";
    t2 = SwatchIcon;
    t3 = attributes.length > 0 ? <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 lg:tw-grid-cols-4">{attributes.map(_temp4)}</div> : <EmptyDetailsState>No properties found.</EmptyDetailsState>;
    $[0] = nft;
    $[1] = T0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    T0 = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[10] !== T0 || $[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t7 = <T0 title={t1} icon={t2}>{t3}</T0>;
    $[10] = T0;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <div className={t4}>{t5}{t6}{t7}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function _temp4(attribute) {
  return <MetadataCard key={`${attribute.trait_type}-${attribute.value}`} label={attribute.trait_type} value={attribute.value} />;
}
function _temp3(row_0) {
  return <MetricBlock key={row_0.key} label={row_0.label} value={row_0.value} />;
}
function _temp2(row) {
  return <ArweaveLinkRow row={row} key={`${row.label}-${row.url}`} />;
}
export function MemeLabOverviewDetails(t0) {
  const $ = _c(11);
  const {
    nft,
    defaultAdditionalDetailsOpen
  } = t0;
  let t1;
  if ($[0] !== nft) {
    t1 = <MemeLabCardDescription nft={nft} />;
    $[0] = nft;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = defaultAdditionalDetailsOpen ? "details-open" : "details-closed";
  let t3;
  if ($[2] !== nft) {
    t3 = <MemeLabAdditionalDetailsContent nft={nft} />;
    $[2] = nft;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== defaultAdditionalDetailsOpen || $[5] !== t2 || $[6] !== t3) {
    t4 = <MemeLabAdditionalDetailsAccordion key={t2} defaultOpen={defaultAdditionalDetailsOpen}>{t3}</MemeLabAdditionalDetailsAccordion>;
    $[4] = defaultAdditionalDetailsOpen;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <>{t1}{t4}</>;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}